import type { TaskView } from "@scoach/types";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { CompanyLogo } from "../../discovery/CompanyLogo.tsx";

type EditableField = "who" | "what" | "due";

function fmtDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isOverdue(due: string): boolean {
  if (!due) return false;
  return new Date(due) < new Date() && due !== new Date().toISOString().slice(0, 10);
}

/**
 * A single task row: company logo, task text, client + meeting link + meeting date +
 * type, owner, and due date. Text/owner/due are click-to-edit. Self-contained editing
 * so it can be reused on the Tasks screen and in the client-tasks popup.
 */
export function TaskRow({
  task,
  onToggle,
  onUpdate,
  showClient = true,
}: {
  task: TaskView;
  onToggle: (task: TaskView, done: boolean) => void;
  onUpdate: (task: TaskView, patch: Partial<Pick<TaskView, "who" | "what" | "due">>) => void;
  showClient?: boolean;
}) {
  const [editField, setEditField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState("");

  function start(field: EditableField) {
    setEditField(field);
    setDraft(task[field] ?? "");
  }
  function commit() {
    if (!editField) return;
    const value = draft.trim();
    if (value !== (task[editField] ?? "")) onUpdate(task, { [editField]: value });
    setEditField(null);
  }

  const overdue = isOverdue(task.due) && !task.done;

  return (
    <div className={`task-card ${task.done ? "done" : ""}`}>
      <input
        type="checkbox"
        className="task-card-check"
        checked={task.done}
        onChange={() => onToggle(task, !task.done)}
      />
      <CompanyLogo name={task.client} domain={task.companyDomain} size={30} rounded={8} />
      <div className="task-card-main">
        {editField === "what" ? (
          <input
            type="text"
            className="task-edit-input"
            value={draft}
            // biome-ignore lint/a11y/noAutofocus: focus the field the user clicked to edit
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setEditField(null);
            }}
            onBlur={commit}
          />
        ) : (
          <button type="button" className="task-card-what" onClick={() => start("what")}>
            {task.what || "—"}
          </button>
        )}
        <div className="task-card-meta">
          {showClient && <span className="task-card-client">{task.client}</span>}
          <Link
            to="/meetings/$id/summary"
            params={{ id: task.meetingId }}
            className="task-card-mtg"
          >
            {task.meetingTitle}
          </Link>
          {task.meetingDate && <span className="task-card-date">{fmtDate(task.meetingDate)}</span>}
          {task.meetingType && task.meetingType !== "sales" && (
            <span className="task-card-type">{task.meetingType}</span>
          )}
          {task.assigneeName && (
            <span
              className="task-card-type"
              style={{
                background: "rgba(99, 102, 241, 0.12)",
                color: "var(--primary)",
                fontWeight: 600,
              }}
              title={task.assigneeEmail || task.assigneeName}
            >
              👤 {task.assigneeName}
            </span>
          )}
          {editField === "who" ? (
            <input
              type="text"
              className="task-edit-input task-edit-who"
              value={draft}
              // biome-ignore lint/a11y/noAutofocus: focus the field the user clicked to edit
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setEditField(null);
              }}
              onBlur={commit}
            />
          ) : (
            <button type="button" className="task-card-who" onClick={() => start("who")}>
              {task.who ? `@${task.who}` : "+ owner"}
            </button>
          )}
        </div>
      </div>
      {editField === "due" ? (
        <input
          type="date"
          className="task-edit-input task-edit-due"
          value={draft}
          // biome-ignore lint/a11y/noAutofocus: focus the field the user clicked to edit
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditField(null);
          }}
          onBlur={commit}
        />
      ) : (
        <button
          type="button"
          className={`task-card-due ${overdue ? "overdue" : ""}`}
          onClick={() => start("due")}
        >
          {task.due ? fmtDate(task.due) : "Set due"}
        </button>
      )}
    </div>
  );
}
